import React, { useEffect, useState } from "react";
import { API_BASE_URL } from "./config";
import "./GlobalJobsCandidateProfile.css";

const emptyForm = {
  full_name: "",
  phone: "",
  country: "",
  city: "",
  education: "",
  current_role: "",
  experience_years: "0",
  skills: "",
  linkedin: "",
  github: "",
  portfolio: "",
  preferred_work_mode: "",
  preferred_job_location: "",
};

const profileFields = [
  ["full_name", "Full Name", true],
  ["phone", "Phone Number (with country code)", false],
  ["country", "Country", true],
  ["city", "City", false],
  ["education", "Highest Education", false],
  ["current_role", "Current / Most Recent Role", false],
  ["experience_years", "Total Experience (Years)", false],
  ["skills", "Technology Skills (comma-separated)", true],
  ["linkedin", "LinkedIn Profile (HTTPS URL)", false],
  ["github", "GitHub Profile (HTTPS URL)", false],
  ["portfolio", "Portfolio (HTTPS URL)", false],
  ["preferred_job_location", "Preferred Job Location", false],
];

async function profileRequest(token, method = "GET", payload = null) {
  const response = await fetch(
    `${API_BASE_URL}/global-tech-jobs/candidate/profile`,
    {
      method,
      headers: {
        Authorization: `Bearer ${token}`,
        ...(payload ? { "Content-Type": "application/json" } : {}),
      },
      ...(payload ? { body: JSON.stringify(payload) } : {}),
    }
  );

  let data = {};

  try {
    data = await response.json();
  } catch {
    // Display the HTTP status below if there is no JSON body.
  }

  if (!response.ok) {
    const detail = data.detail;
    throw new Error(
      typeof detail === "string"
        ? detail
        : `Profile request failed (${response.status}).`
    );
  }

  return data;
}

export default function GlobalJobsCandidateProfile({ token }) {
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError("");

      try {
        const data = await profileRequest(token);

        if (cancelled) return;

        const next = { ...emptyForm, ...data.profile };

        next.skills = Array.isArray(next.skills)
          ? next.skills.join(", ")
          : String(next.skills || "");

        next.experience_years = String(
          next.experience_years ?? 0
        );

        setForm(next);
        setCompleted(Boolean(data.profile_completed));
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    if (token) load();

    return () => {
      cancelled = true;
    };
  }, [token]);

  const changeField = (name, value) => {
    setForm((previous) => ({ ...previous, [name]: value }));
  };

  const submit = async (event) => {
    event.preventDefault();

    if (saving) return;

    setSaving(true);
    setError("");
    setNotice("");

    try {
      const experience = Number(form.experience_years);

      if (!Number.isFinite(experience) || experience < 0 || experience > 60) {
        throw new Error("Enter a valid experience in years.");
      }

      const payload = {
        ...form,
        experience_years: experience,
        skills: form.skills
          .split(",")
          .map((skill) => skill.trim())
          .filter(Boolean),
      };

      if (!payload.skills.length) {
        throw new Error("Enter at least one technology skill.");
      }

      const data = await profileRequest(token, "PUT", payload);

      setCompleted(Boolean(data.profile_completed));
      setNotice("Profile saved successfully.");
      setEditing(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="gtjp-panel">
      <div className="gtjp-heading">
        <div>
          <span>CANDIDATE CAREER PROFILE</span>
          <h3>My Professional Profile</h3>
          <p>
            Keep your experience, skills and career preferences
            updated for future job applications and AI matching.
          </p>
        </div>

        <button
          type="button"
          onClick={() => setEditing((value) => !value)}
          disabled={loading || saving}
        >
          {editing ? "Close Editor" : completed ? "Edit Profile" : "Complete Profile"}
        </button>
      </div>

      {loading ? (
        <p>Loading your profile...</p>
      ) : (
        <>
          <div className="gtjp-status">
            {completed
              ? "Profile essentials completed"
              : "Complete your profile before applying to jobs"}
          </div>

          {error && <p className="gtjp-error">{error}</p>}
          {notice && <p className="gtjp-success">{notice}</p>}

          {!editing ? (
            <div className="gtjp-summary">
              <div>
                <small>Full Name</small>
                <strong>{form.full_name || "Not provided"}</strong>
              </div>
              <div>
                <small>Country</small>
                <strong>{form.country || "Not provided"}</strong>
              </div>
              <div>
                <small>Experience</small>
                <strong>{form.experience_years} years</strong>
              </div>
              <div>
                <small>Skills</small>
                <strong>{form.skills || "Not provided"}</strong>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="gtjp-form">
              <div className="gtjp-grid">
                {profileFields.map(([name, label, required]) => (
                  <label
                    key={name}
                    className={name === "skills" ? "gtjp-wide" : ""}
                  >
                    {label}
                    <input
                      type={name === "experience_years" ? "number" : "text"}
                      min={name === "experience_years" ? "0" : undefined}
                      max={name === "experience_years" ? "60" : undefined}
                      step={name === "experience_years" ? "0.5" : undefined}
                      value={form[name]}
                      onChange={(event) =>
                        changeField(name, event.target.value)
                      }
                      required={required}
                    />
                  </label>
                ))}

                <label>
                  Preferred Work Model
                  <select
                    value={form.preferred_work_mode}
                    onChange={(event) =>
                      changeField("preferred_work_mode", event.target.value)
                    }
                  >
                    <option value="">Not specified</option>
                    <option value="any">Any</option>
                    <option value="remote">Remote</option>
                    <option value="hybrid">Hybrid</option>
                    <option value="semi_hybrid">Semi-Hybrid</option>
                    <option value="onsite">On-site</option>
                  </select>
                </label>
              </div>

              <div className="gtjp-form-actions">
                <button type="submit" disabled={saving}>
                  {saving ? "Saving..." : "Save Profile"}
                </button>
              </div>
            </form>
          )}
        </>
      )}
    </section>
  );
}
